import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { BuyerModeContext } from './BuyerModeContext';
import api from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { clearMapCaches, readMapCache } from '../mapHome/mapData';

// mid-uhy.24: the map-sized summary is the only source of the mode, and the provider
// below is fed by every fetch of it (the shell's bootstrap, the onboarding's mode check),
// so a route outside the shell never asks on its own. Concurrent callers share one request.
let feed = null;
let inflight = null;
export const fetchCockpitSummary = () => {
  if (!inflight) {
    inflight = api.get('/v3/buyer/cockpit_summary', { params: { view: 'map' } }).finally(() => { inflight = null; });
    inflight.then(({ data }) => feed?.(data?.mode || null), () => {});
  }
  return inflight;
};

// Mounted once above the routes (App.js): the mode survives navigation between the
// cockpit, a property detail and the onboarding. Seeded from the return-visit map
// cache, else by one summary request; reset when the buyer logs out.
export function BuyerModeProvider({ children }) {
  const user = useAuth().user;
  const userId = user?.user_type === 'buyer' ? user.user_id : null;
  const [modeInfo, setModeInfo] = useState(null);
  const [switched, setSwitched] = useState(null);

  useEffect(() => {
    feed = setModeInfo;
    return () => { feed = null; };
  }, []);

  useEffect(() => {
    if (!userId) { setModeInfo(null); return; }
    const cached = readMapCache(userId)?.summary;
    if (cached) setModeInfo(cached.mode || null);
    else fetchCockpitSummary().catch(() => {});
  }, [userId]);

  // POST /mode is the switch; its answer is the new mode truth. The cached map is the
  // old mode's listings, so it goes before the shell's keyed MapHome repaints.
  const switchMode = useCallback(async (next) => {
    try {
      const { data } = await api.post('/v3/buyer/mode', { mode: next });
      trackEvent('buyer_mode_switched', { mode: next });
      clearMapCaches();
      setModeInfo(data);
      setSwitched(next);
      return true;
    } catch {
      toast.error('Não consegui mudar de modo.');
      return false;
    }
  }, []);

  const value = useMemo(() => (modeInfo ? { ...modeInfo, switchMode, switched } : null), [modeInfo, switchMode, switched]);
  return <BuyerModeContext.Provider value={value}>{children}</BuyerModeContext.Provider>;
}
